import { queueOpsApi } from "@/shared/api/queueOps";
import { Button } from "@/components/ui/button";

import type { AppointmentActionGuard } from "../hooks/useAppointmentActionGuard";

/**
 * Gọi lại · Tạm giữ · Bỏ qua — the doctor's queue quick actions (TD-7b, #18800/#18807).
 *
 * <p><b>It consumes the guard; it cannot define it.</b> The props are a value ({@code busy}) and a
 * verb ({@code run}) — there is no setter among them, so this component cannot decide when the
 * appointment is free. That was the whole reason #18800 refused to extract it before #18807
 * answered who owns the state: handing a panel {@code setQueueBusy} would have moved the rule out
 * of the one place that should hold it while pretending to decouple.
 *
 * <p>The guard instance stays in the workspace because the fact really is shared — the sign button
 * is disabled by the same flag, since signing and holding the same patient must not race.
 */
export function QueueActions({
  appointmentId,
  guard,
}: {
  appointmentId: string;
  guard: AppointmentActionGuard;
}) {
  return (
    <div className="mr-1 flex items-center gap-1.5 border-r pr-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={guard.busy}
        title="Gọi lại bệnh nhân hiện tại (không đổi hàng đợi)"
        onClick={() =>
          guard.run(() => queueOpsApi.recall(appointmentId), "Đã gọi lại bệnh nhân.")
        }
      >
        Gọi lại
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={guard.busy}
        title="Tạm giữ — bệnh nhân đi làm CLS/XN rồi quay lại"
        onClick={() =>
          guard.run(
            () => queueOpsApi.hold(appointmentId),
            "Đã tạm giữ (bệnh nhân sẽ quay lại)."
          )
        }
      >
        Tạm giữ
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={guard.busy}
        title="Bỏ qua bệnh nhân hiện tại (có thể gọi lại sau)"
        onClick={() => guard.run(() => queueOpsApi.skip(appointmentId), "Đã bỏ qua bệnh nhân.")}
      >
        Bỏ qua
      </Button>
    </div>
  );
}
